import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

export default function Camera({ onPhotoTaken }) {
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async () => {
    try {
      setCameraError(null);
      setShowCamera(true);

      await new Promise((resolve) => setTimeout(resolve, 500));

      if (!videoRef.current) {
        throw new Error('無法初始化相機');
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const getUserMedia = navigator.getUserMedia ||
          navigator.webkitGetUserMedia ||
          navigator.mozGetUserMedia ||
          navigator.msGetUserMedia;

        if (!getUserMedia) {
          throw new Error('您的瀏覽器不支援相機功能');
        }

        getUserMedia.call(navigator, { video: true }, (stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        }, (err) => {
          throw new Error(`無法開啟相機: ${err.message}`);
        });
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            facingMode: 'environment',
          },
        });

        videoRef.current.srcObject = stream;

        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            resolve();
          };
        });
      }
    } catch (err) {
      setCameraError(err.message ? `無法開啟相機: ${err.message}` : '無法開啟相機，請確認已授予相機權限');
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
    setCameraError(null);
  };

  const takePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // 使用相機的完整解析度
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // 直接捕獲完整畫面
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
        onPhotoTaken(file);
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          onClick={startCamera}
          disabled={showCamera}
        >
          開啟相機
        </Button>
      </Grid>
      {cameraError && (
        <Grid item xs={12}>
          <Typography color="error">
            {cameraError}
          </Typography>
        </Grid>
      )}
      {showCamera && (
        <Grid item xs={12}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 640, margin: '0 auto' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                borderRadius: 8,
                backgroundColor: '#000',
                aspectRatio: '4/3',
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={takePhoto}
                style={{ marginRight: 8 }}
              >
                拍照
              </Button>
              <Button
                variant="outlined"
                onClick={stopCamera}
              >
                關閉相機
              </Button>
            </div>
          </div>
        </Grid>
      )}
    </Grid>
  );
}

Camera.propTypes = {
  onPhotoTaken: PropTypes.func.isRequired,
};
